import { useQuery } from '@tanstack/react-query';
import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useState } from 'react';

import { getTradingCompetitionRankingsApi } from '@flipster/apis/v1/tradingCompetition';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { FormattedMessage } from '@flipster/i18n';
import { createSingleChoice } from '@flipster/react/design-system/chip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import { css } from '@flipster/styled-system/css';
import { flex, grid, hstack, vstack } from '@flipster/styled-system/patterns';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getNumberArr } from '@flipster/utils/num';

import { MaxWidthSection } from '@web/app/[locale]/(web)/campaign/_section/_layout';
import { ImgOrLottie } from '@web/app/[locale]/(web)/campaign/_section/misc/img-or-lottie';

const Choice = createSingleChoice<'pnl' | 'volume'>();
// 임시 페이지
// tc 랭크를 임시로 보여줌
interface Props {
  tabs: { id: string; value: 'pnl' | 'volume' }[];
}

export const HallOfFame = ({ tabs }: Props) => {
  const { isCookieLoggedInAtom } = useBunja(authBunja);

  const isCookieLoggedIn = useAtomValue(isCookieLoggedInAtom);
  const [tab, setTab] = useState<'pnl' | 'volume'>(tabs[0]?.value);

  const tcId = tabs.find(({ value }) => tab === value)?.id;

  const { data } = useQuery({
    queryKey: ['getTradingCompetitionRankingsApi', tcId, isCookieLoggedIn],
    queryFn: () => getTradingCompetitionRankingsApi(tcId!),
    select: (res) => res.data,
    refetchOnMount: true,
    enabled: !!tcId,
  });

  const myRanking = data?.myRanking;

  return (
    <MaxWidthSection sectionType='hall-of-fame'>
      <h3 className={css({ textStyle: 'Display/L', color: 'content.accent.promotion', mb: 16 })}>
        <FormattedMessage id='Campaign.r251127.Hall_of_Fame' />
      </h3>
      <Choice value={tab} onChange={setTab} css={{ mb: 16 }}>
        {tabs.map((t) => (
          <Choice.Chip label={labels[t.value]} value={t.value} key={t.value} />
        ))}
      </Choice>
      <ul className={flex({ flexDirection: { base: 'column', tablet: 'row' }, alignItems: 'flex-end', gap: 24 })}>
        {getNumberArr(3).map((value) => {
          const currentRanker = data?.rankings[value];

          return (
            <li
              className={css(
                vstack.raw({
                  p: 24,
                  bg: 'background.default.level-3',
                  color: 'content.default.level-5',
                  rounded: 16,
                  flex: 1,
                  w: '100%',
                  order: { tablet: tabletOrders[value] },
                }),
                value === 0 && { h: { tablet: 390 } },
                value !== 0 && { h: { tablet: 340 } },
              )}
              key={value}
            >
              <ImgOrLottie
                url={rankLotties[value]}
                css={css.raw(
                  { w: 120, h: 120 },
                  value === 0 && { w: { base: 120, tablet: 170 }, h: { base: 120, tablet: 170 } },
                )}
              />
              <p className={css({ textStyle: 'Title/M', textAlign: 'center', mt: 24, mb: 8 })}>Top {value + 1}</p>
              {currentRanker?.nickname ? (
                <p className={hstack({ textStyle: 'Title/S', mb: 24 })}>
                  {currentRanker.nickname}
                  <IconButton
                    hierarchy='quaternary'
                    size='small'
                    icon={<ChevronRightIcon />}
                    as='a'
                    href={`/user/profile/${currentRanker.userProfileId}`}
                    newTab
                  />
                </p>
              ) : (
                <p className={css({ textStyle: 'Title/S', mb: 24 })}>calculating..</p>
              )}
              <dl
                className={grid({
                  columns: 2,
                  w: '100%',
                  gap: 8,
                  '& > dt': { textStyle: 'Text/M/Regular', color: 'content.default.level-3' },
                  '& > dd': { textStyle: 'Title/XS', textAlign: 'right' },
                })}
              >
                <dt>Prize</dt>
                <dd>
                  <Formatted.Number
                    value={prizes[tab]?.[value]}
                    suffix={UsdtCurrency}
                    css={{ color: 'content.accent.VIP' }}
                  />
                </dd>
                <dt>{labels[tab]}</dt>
                <dd>
                  <Formatted.Number
                    value={currentRanker?.[tab]}
                    precision={UsdtPrecision}
                    suffix={UsdtCurrency}
                    withColor={tab === 'pnl'}
                  />
                </dd>
              </dl>
            </li>
          );
        })}
      </ul>
      {myRanking && (
        <dl
          className={grid({
            textStyle: 'Title/S',
            columns: 2,
            mt: 32,
            p: 16,
            gap: 16,
            rounded: 8,
            bg: 'background.default.level-3',
            '& > dt': { color: 'content.accent.promotion' },
            '& > dd': { color: 'content.default.level-5', textAlign: 'right' },
          })}
        >
          <dt>Your volume</dt>
          <dd>
            <Formatted.Number value={myRanking.volume} precision={UsdtPrecision} />
          </dd>
          <dt>Your P&L</dt>
          <dd>
            <Formatted.Number value={myRanking.pnl} precision={UsdtPrecision} withColor />
          </dd>
        </dl>
      )}
      <p
        className={css({
          w: '100%',
          textStyle: 'Text/L/Regular',
          color: 'content.default.level-2',
          textAlign: 'right',
          mt: 8,
        })}
      >
        *<FormattedMessage id='Campaign.r251127.Updated_every_10m' />
      </p>
    </MaxWidthSection>
  );
};

const labels: Record<string, string> = {
  volume: 'Volume',
  pnl: 'Profit & Loss',
};

const tabletOrders = [1, 0, 2];

const rankLotties = [
  'https://lottie.host/364d0203-5eda-4d8f-bf54-947e35cd67af/MXCx4ED7Y9.lottie',
  'https://lottie.host/2d24d465-9ab8-4247-9461-45fea3df5ffc/MYuwmWhAod.lottie',
  'https://lottie.host/b81fd483-cafd-4a1d-9598-21f4f0ca836d/84pfhwfbOD.lottie',
];

const prizes: Record<string, number[]> = {
  volume: [3500, 2000, 1500],
  pnl: [2500, 1500, 1000],
};
